import { useEffect, useMemo, useRef } from 'react';
import type { BrandProfile } from '../lib/types';

// A tool's settings (tone, language, model look...) follow the brand profile until the
// merchant changes them. A field counts as unchanged while it holds the tool's built-in
// default, the profile's value, or the value the profile gave it last time, so a restored
// draft keeps what the merchant picked, and an edit to the profile reaches fields they left alone.

const STORAGE_PREFIX = 'brand-defaults-applied';

type Fields = Record<string, unknown>;

function same(a: unknown, b: unknown): boolean {
  const stable = (value: unknown) =>
    value && typeof value === 'object'
      ? JSON.stringify(value, Object.keys(value as object).sort())
      : JSON.stringify(value);
  return stable(a) === stable(b);
}

function readApplied(userId: string, tool: string): Fields {
  try {
    const all = JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}:${userId}`) || '{}');
    return all[tool] || {};
  } catch {
    return {};
  }
}

function writeApplied(userId: string, tool: string, applied: Fields) {
  try {
    const key = `${STORAGE_PREFIX}:${userId}`;
    const all = JSON.parse(localStorage.getItem(key) || '{}');
    all[tool] = applied;
    localStorage.setItem(key, JSON.stringify(all));
  } catch {
    // Without storage the profile is simply applied again on the next visit.
  }
}

// Returns `value` when it is one of `options`, so a saved value a tool doesn't offer is ignored.
export function knownValue<V extends string>(options: { value: V }[], value: string | null | undefined): V | undefined {
  return options.find((option) => option.value === value)?.value;
}

interface BrandDefaultsOptions<T extends Fields> {
  // Name for this tool's record of what the profile last applied.
  tool: string;
  // The tool's own defaults for these fields.
  builtIn: T;
  // What the form holds now.
  current: T;
  // The profile's value for each field; leave a field out (or undefined) when the profile doesn't set it.
  fromProfile: (profile: BrandProfile) => Partial<T>;
  apply: (updates: Partial<T>) => void;
}

// Applies the profile once it has loaded. Returns a ref to the defaults in effect
// (built-in, overridden by the profile) for "reset" handlers to use.
export function useBrandDefaults<T extends Fields>(
  brand: { profile: BrandProfile | null; loading: boolean; userId: string | null },
  { tool, builtIn, current, fromProfile, apply }: BrandDefaultsOptions<T>
) {
  const { profile, loading, userId } = brand;

  const profileValues = useMemo(() => {
    const values = profile ? fromProfile(profile) : {};
    return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined)) as Partial<T>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const defaultsRef = useRef<T>(builtIn);
  defaultsRef.current = { ...builtIn, ...profileValues };

  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || loading || appliedFor.current === userId) return;
    appliedFor.current = userId;

    const previouslyApplied = readApplied(userId, tool);
    const nowApplied: Fields = {};
    const updates: Partial<T> = {};

    for (const key of Object.keys(builtIn) as (keyof T & string)[]) {
      const unchanged = same(current[key], builtIn[key]) ||
        (key in profileValues && same(current[key], profileValues[key])) ||
        (key in previouslyApplied && same(current[key], previouslyApplied[key]));
      if (!unchanged) continue;

      if (key in profileValues) {
        nowApplied[key] = profileValues[key];
        if (!same(current[key], profileValues[key])) updates[key] = profileValues[key];
      } else if (!same(current[key], builtIn[key])) {
        // The profile no longer sets this field, so it goes back to the tool's default.
        updates[key] = builtIn[key];
      }
    }

    writeApplied(userId, tool, nowApplied);
    if (Object.keys(updates).length > 0) apply(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loading]);

  return defaultsRef;
}
